// CSRF ANALYSIS: This app is NOT vulnerable to CSRF attacks because:
// 1. access_token is stored in memory (not cookies), sent via Authorization: Bearer header
// 2. Browsers do NOT automatically include Authorization headers in cross-origin requests
// 3. refresh_token cookie uses SameSite=Strict, preventing cross-site sending
// 4. Only /auth/refresh uses cookies, and it's protected by SameSite=Strict

import axios from 'axios'
import * as Sentry from '@sentry/react'
import { toast } from '@/shared/utils/toast'
import { env, parseApiError, shouldLogoutForCode, storage } from '@/shared'
import { showLimitReachedDialog } from '@/components/subscription/limit-reached-dialog'

const AUTH_PERMISSION_DENIED = 'AUTH_PERMISSION_DENIED'

const SUBSCRIPTION_LIMIT_REACHED = 'SUBSCRIPTION_LIMIT_REACHED'
const SUBSCRIPTION_PLAN_REQUIRED = 'SUBSCRIPTION_PLAN_REQUIRED'

export const api = axios.create({
  baseURL: env.apiUrl,
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = storage.getToken()
  const tenantId = storage.getTenantId()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Super-admin pode operar em outras empresas via header X-Tenant-Id
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const apiError = parseApiError(error)
    const errorCode = error.response?.data?.code

    const isLoginRequest = originalRequest?.url?.includes('/auth/login')

    if (shouldLogoutForCode(apiError?.code) && !isLoginRequest) {
      storage.clearAll()
      window.location.href = '/login'

      return Promise.reject(error)
    }

    if (apiError?.code === AUTH_PERMISSION_DENIED) {
      toast.error('Acesso negado', {
        description: apiError.message ?? 'Você não tem permissão para acessar este recurso.',
      })

      return Promise.reject(error)
    }

    // Tratamento de limites de assinatura (HTTP 403)
    if (
      error.response?.status === 403 &&
      (errorCode === SUBSCRIPTION_LIMIT_REACHED || errorCode === SUBSCRIPTION_PLAN_REQUIRED)
    ) {
      const message = error.response?.data?.message

      showLimitReachedDialog(message)

      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Refresh token is sent automatically via httpOnly cookie (withCredentials: true)
        const { data } = await axios.post(
          `${env.apiUrl}/auth/refresh`,
          {},
          { withCredentials: true },
        )

        storage.setToken(data.access_token)

        originalRequest.headers.Authorization = `Bearer ${data.access_token}`

        return api(originalRequest)
      } catch (refreshError) {
        storage.clearAll()
        window.location.href = '/login'

        return Promise.reject(refreshError)
      }
    }

    // Capturar erros 5xx no Sentry (erros do servidor)
    // Cria um novo Error com apenas a mensagem para evitar vazamento de dados sensíveis
    // (headers Authorization, body com senhas/PII) que estariam no objeto error do Axios
    if (error.response?.status >= 500) {
      Sentry.captureException(new Error(error.message), {
        tags: {
          api_url: originalRequest?.url?.split('?')[0],
          http_status: error.response.status,
          error_code: errorCode,
        },
      })
    }

    return Promise.reject(error)
  },
)
